import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { REGISTER_TRADEMARK_CODE_ARRAY } from "_constants";
import breakpoints from "_constants/breakpoints";

import { getProductsFiles } from "_helpers";

import { ordersActions } from "_store/actions";

import GeneralInfo from "components/OrderDetail/GeneralInfo";
import Documents from "components/OrderDetail/Documents";

import { ErrorModal } from "components/Modals/ErrorModal";

import { SpinnerWrapper } from "widget/Spinner";
import { constantActions } from "_store/actions";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 500px;

  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }
`;
const Title = styled.h2`
  margin-bottom: 14px;
  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  color: #03589c;

  @media (max-width: ${breakpoints.mobileM}) {
    margin-left: 0px;
    font-size: 18px;
  }
`;
const Info = styled.div`
  max-width: 1376px;
  width: 90%;
  padding: 12px 6px;
  text-align: justify;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          padding: 0 16px 12px;

          @media (max-width: ${breakpoints.mobileM}) {
            font-size: 14px;
          }
        `
      : css`
          @media (max-width: 1350px) {
            width: 90%;
          }
        `}
`;
const Button = styled.button`
  width: 209px;
  height: 42px;
  margin-bottom: 24px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;
`;

export default function Specimen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderDetails, orderDetailsLoading, requestLoading, error } = useSelector(({ orders }) => orders);

  const { orderId } = useParams();

  const product = orderDetails?.products.find(({ code }) => REGISTER_TRADEMARK_CODE_ARRAY.includes(code.code));
  const productId = product?.id;

  const [newProductInfo, setNewProductInfo] = useState(product);

  const productsFiles = getProductsFiles(orderDetails?.products, [], ["TRADEMARK_SPECIMEN"]);

  const handleChangeProductRegister = ({ target: { name, value } }) => {
    let newValue = value;

    if (value === "") {
      newValue = null;
    }

    setNewProductInfo({ ...newProductInfo, [name]: newValue });
  };

  const handleUploadAndUpdate = (formData) => {
    dispatch(
      ordersActions.postSpecimenUpdateProduct({
        orderId,
        productId,
        product: newProductInfo,
        formData,
        fileType: "TRADEMARK_SPECIMEN",
        handleCallback: () => {
          dispatch(ordersActions.getOrders());
        },
      })
    );
  };

  const handleUpdate = () => {
    dispatch(
      ordersActions.putProduct({
        orderId,
        productId,
        product: newProductInfo,
        handleCallback: () => {
          dispatch(ordersActions.getOrders());
        },
      })
    );
  };

  useEffect(() => dispatch(ordersActions.getOrder(orderId)), [orderId, dispatch]);

  useEffect(() => {
    if (productId) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      });
    }
  }, [productId]);

  useEffect(() => {
    const product = orderDetails?.products.find(({ code }) => REGISTER_TRADEMARK_CODE_ARRAY.includes(code.code));

    if (product) {
      dispatch(constantActions.getProductFileTypes([product?.code?.code]));
    }

    setNewProductInfo(product);
  }, [orderDetails]);

  if (!orderDetails)
    return (
      <Container key="container-speciman-alert">
        {(orderDetailsLoading && <SpinnerWrapper />) || (error && <ErrorModal />)}
      </Container>
    );

  if (!productId) {
    navigate(`/orders/${orderId}`);

    return null;
  }

  return (
    <Container key="container-speciman-main">
      <GeneralInfo orderDetails={orderDetails} />
      <Title>Specimen</Title>
      <Info>
        Our specialists have received your order. To begin creating your application, the USPTO requires a specimen to
        be submitted along with your information. <br />
        <b>What is a specimen?</b>
        <br /> * Real-world application example of how the trademark is used with the goods/services the company
        provides.
        <br /> * Some acceptable specimen examples: Tags, Packaging, magazines, website pages, etc. Once we receive your
        specimen and you have had 24 hours to review your selected search results, we will begin processing your
        application.
      </Info>
      <Documents
        actions="SPECIMEN"
        isDisabled={!!requestLoading}
        order={orderDetails}
        orderId={orderId}
        product={newProductInfo}
        productId={productId}
        files={productsFiles}
        onChange={handleChangeProductRegister}
        onUpload={handleUploadAndUpdate}
        onUpdate={handleUpdate}
      />
      <Button onClick={() => navigate(`/orders/${orderId}`)}>Go Back</Button>
    </Container>
  );
}
